import { useCallback, useEffect, useMemo, useState } from 'react'
import { UtensilsCrossed, Loader2, RefreshCw, Heart, Plus, X, Search, CheckCircle2, Undo2 } from 'lucide-react'
import type { TandoorMealDto, TandoorRecipeDto, MealWishDto } from '@familynest/shared'
import { AuthImage } from '../AuthImage'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../store/auth'
import { Button, useBackdropClose } from '../ui'

const MAX_WISHES = 3

function dayKey(iso: string): string {
  return iso.slice(0, 10)
}
function dayLabel(iso: string): string {
  const d = new Date(iso)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const dd = new Date(d)
  dd.setHours(0, 0, 0, 0)
  const diff = Math.round((dd.getTime() - today.getTime()) / 86400000)
  if (diff === 0) return 'Heute'
  if (diff === 1) return 'Morgen'
  return d.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })
}

/**
 * Mahlzeiten — read-only Tandoor-Speiseplan über den Backend-Proxy (ADR-034),
 * gruppiert nach Tag. Erwachsene können die zugehörige Tandoor-Einkaufsliste in
 * die native Liste „Einkaufsliste aus Speiseplan" synchronisieren.
 */
export function MealsScreen(): React.JSX.Element {
  const { member } = useAuth()
  const isAdult = member?.role === 'adult'
  const canWish = member?.role !== 'guest'
  const [configured, setConfigured] = useState<boolean | null>(null)
  const [meals, setMeals] = useState<TandoorMealDto[]>([])
  const [wishes, setWishes] = useState<MealWishDto[]>([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const st = await api.integrationsStatus()
      setConfigured(st.tandoor)
      if (st.tandoor) {
        const [m, w] = await Promise.all([api.tandoorMealPlan(), api.tandoorWishes()])
        setMeals(m)
        setWishes(w)
      }
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  const loadWishes = useCallback(async () => setWishes(await api.tandoorWishes()), [])
  async function removeWish(id: string): Promise<void> {
    await api.tandoorRemoveWish(id)
    await loadWishes()
  }
  async function fulfillWish(id: string, fulfilled: boolean): Promise<void> {
    await api.tandoorFulfillWish(id, fulfilled)
    await loadWishes()
  }
  // Nur OFFENE Wünsche zählen gegen das Limit (erfüllte geben den Slot frei).
  const myWishCount = wishes.filter((w) => w.wishedBy === member?.id && !w.fulfilledAt).length

  const days = useMemo(() => {
    const map = new Map<string, TandoorMealDto[]>()
    for (const m of meals) {
      const k = dayKey(m.date)
      const arr = map.get(k) ?? []
      arr.push(m)
      map.set(k, arr)
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b))
  }, [meals])

  async function sync(): Promise<void> {
    setSyncing(true)
    setMsg(null)
    try {
      const r = await api.tandoorSyncShopping()
      setMsg(`Einkaufsliste aktualisiert: ${r.open} Positionen offen (${r.added} neu, ${r.removed} entfernt). Zu finden unter „Listen" → „Einkaufsliste aus Speiseplan".`)
    } catch {
      setMsg('Aktualisierung fehlgeschlagen (Tandoor nicht erreichbar?).')
    } finally {
      setSyncing(false)
    }
  }

  if (configured === null || loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-6 animate-spin" style={{ color: 'var(--color-muted)' }} />
      </div>
    )
  }
  if (!configured) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center text-sm" style={{ color: 'var(--color-muted)' }}>
        <UtensilsCrossed className="size-8" />
        <p className="max-w-sm">Mahlzeiten sind noch nicht eingerichtet (Tandoor-Zugang fehlt serverseitig).</p>
      </div>
    )
  }

  return (
    <div className="flex min-h-full flex-col gap-3 p-3 md:p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>Mahlzeiten</h1>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canWish && (
            <Button onClick={() => setPickerOpen(true)} disabled={myWishCount >= MAX_WISHES}>
              <Heart className="size-4" /> Wunschgericht ({myWishCount}/{MAX_WISHES})
            </Button>
          )}
          {isAdult && (
            <Button variant="ghost" onClick={sync} disabled={syncing}>
              <RefreshCw className={`size-4 ${syncing ? 'animate-spin' : ''}`} /> Einkaufsliste aktualisieren
            </Button>
          )}
        </div>
      </div>
      {msg && (
        <p className="rounded-lg border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-line)', backgroundColor: 'var(--color-surface)', color: 'var(--color-ink)' }}>{msg}</p>
      )}

      {/* Wunschliste */}
      <section className="rounded-[var(--radius-card)] border p-3" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}>
        <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--color-ink)' }}>
          <Heart className="size-4" style={{ color: 'var(--color-accent)' }} /> Wunschliste
        </h2>
        {wishes.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--color-muted)' }}>Noch keine Wunschgerichte. Tippe „Wunschgericht", um eins aus den Rezepten zu wählen.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {wishes.map((w) => {
              const canRemove = isAdult || w.wishedBy === member?.id
              const done = w.fulfilledAt !== null
              return (
                <li key={w.id} className="flex items-center gap-3" style={{ opacity: done ? 0.6 : 1 }}>
                  {w.imageUrl ? (
                    <AuthImage src={w.imageUrl} alt="" loading="lazy" className="size-10 shrink-0 rounded-lg object-cover" style={{ backgroundColor: 'var(--color-surface)' }} />
                  ) : (
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: 'var(--color-surface)' }}><UtensilsCrossed className="size-4" style={{ color: 'var(--color-muted)' }} /></div>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className={`block truncate text-sm font-medium ${done ? 'line-through' : ''}`} style={{ color: 'var(--color-ink)' }}>{w.recipeName}</span>
                    {done ? (
                      <span className="block text-xs" style={{ color: 'var(--color-success)' }}>✓ erfüllt am {new Date(w.fulfilledAt!).toLocaleDateString('de-DE')}{w.fulfilledByName ? ` von ${w.fulfilledByName}` : ''}</span>
                    ) : (
                      <span className="block text-xs" style={{ color: 'var(--color-muted)' }}>von {w.wishedByName} am {new Date(w.createdAt).toLocaleDateString('de-DE')}</span>
                    )}
                  </span>
                  {isAdult && !done && (
                    <button onClick={() => void fulfillWish(w.id, true)} title="Als erfüllt markieren" className="flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium" style={{ backgroundColor: 'var(--color-success)', color: '#fff' }}>
                      <CheckCircle2 className="size-3.5" /> Erfüllt
                    </button>
                  )}
                  {isAdult && done && (
                    <button onClick={() => void fulfillWish(w.id, false)} title="Erfüllung zurücknehmen" className="rounded p-1 hover:bg-black/5"><Undo2 className="size-4" style={{ color: 'var(--color-muted)' }} /></button>
                  )}
                  {canRemove && (
                    <button onClick={() => void removeWish(w.id)} aria-label="Entfernen" className="rounded p-1 hover:bg-black/5"><X className="size-4" style={{ color: 'var(--color-muted)' }} /></button>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </section>

      {days.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-sm" style={{ color: 'var(--color-muted)' }}>
          <UtensilsCrossed className="size-8" />
          Kein Speiseplan für die nächsten Tage.
        </div>
      ) : (
        days.map(([k, list]) => (
          <section key={k}>
            <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-muted)' }}>{dayLabel(list[0]!.date)}</h2>
            <div className="flex flex-col gap-2">
              {list.map((m) => (
                <div key={m.id} className="flex items-center gap-3 rounded-[var(--radius-card)] border p-2" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}>
                  {m.imageUrl ? (
                    <AuthImage src={m.imageUrl} alt="" loading="lazy" className="size-16 shrink-0 rounded-lg object-cover" style={{ backgroundColor: 'var(--color-surface)' }} />
                  ) : (
                    <div className="flex size-16 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: 'var(--color-surface)' }}>
                      <UtensilsCrossed className="size-6" style={{ color: 'var(--color-muted)' }} />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    {m.mealType && <div className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-accent)' }}>{m.mealType}</div>}
                    <div className="truncate font-medium" style={{ color: 'var(--color-ink)' }}>{m.title}</div>
                    {m.note && <div className="truncate text-xs" style={{ color: 'var(--color-muted)' }}>{m.note}</div>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))
      )}

      {pickerOpen && (
        <RecipePicker
          remaining={MAX_WISHES - myWishCount}
          onClose={() => setPickerOpen(false)}
          onAdded={async () => {
            await loadWishes()
          }}
        />
      )}
    </div>
  )
}

/** Rezept-Suche über den gesamten Tandoor-Bestand → Wunschgericht hinzufügen. */
function RecipePicker({ remaining, onClose, onAdded }: { remaining: number; onClose: () => void; onAdded: () => Promise<void> }): React.JSX.Element {
  const [query, setQuery] = useState('')
  const [recipes, setRecipes] = useState<TandoorRecipeDto[]>([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const full = remaining <= 0

  useEffect(() => {
    let alive = true
    setLoading(true)
    const t = setTimeout(() => {
      void api
        .tandoorRecipes(query)
        .then((r) => {
          if (alive) setRecipes(r.recipes)
        })
        .catch(() => {
          if (alive) setError('Tandoor nicht erreichbar.')
        })
        .finally(() => {
          if (alive) setLoading(false)
        })
    }, 250)
    return () => {
      alive = false
      clearTimeout(t)
    }
  }, [query])

  async function add(r: TandoorRecipeDto): Promise<void> {
    setBusy(r.id)
    setError(null)
    try {
      await api.tandoorAddWish(r.id)
      await onAdded()
    } catch (err) {
      setError(err instanceof ApiError && err.message === 'wish_limit' ? 'Maximum 3 Wunschgerichte erreicht.' : 'Hinzufügen fehlgeschlagen.')
    } finally {
      setBusy(null)
    }
  }

  const backdrop = useBackdropClose(onClose)
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" {...backdrop}>
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border sm:rounded-2xl" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b px-5 py-4" style={{ borderColor: 'var(--color-line)' }}>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>Wunschgericht wählen</h2>
          <button onClick={onClose} aria-label="Schließen" className="rounded-lg p-1 hover:bg-black/5"><X className="size-5" style={{ color: 'var(--color-muted)' }} /></button>
        </div>
        <div className="px-5 py-3">
          <div className="flex items-center gap-2 rounded-lg border px-3 py-2" style={{ borderColor: 'var(--color-line)', backgroundColor: 'var(--color-surface)' }}>
            <Search className="size-4" style={{ color: 'var(--color-muted)' }} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rezept suchen…" autoFocus className="w-full bg-transparent outline-none" style={{ color: 'var(--color-ink)' }} />
          </div>
          {full && <p className="mt-2 text-sm" style={{ color: 'var(--color-danger)' }}>Du hast bereits 3 Wunschgerichte (Maximum). Entferne zuerst eines.</p>}
          {error && <p className="mt-2 text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>}
        </div>
        <div className="flex-1 overflow-auto px-2 pb-3">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="size-5 animate-spin" style={{ color: 'var(--color-muted)' }} /></div>
          ) : recipes.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm" style={{ color: 'var(--color-muted)' }}>Keine Rezepte gefunden.</p>
          ) : (
            recipes.map((r) => (
              <button key={r.id} onClick={() => add(r)} disabled={busy === r.id || full} className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-black/5 disabled:opacity-50">
                {r.imageUrl ? (
                  <AuthImage src={r.imageUrl} alt="" loading="lazy" className="size-10 shrink-0 rounded-lg object-cover" style={{ backgroundColor: 'var(--color-surface)' }} />
                ) : (
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: 'var(--color-surface)' }}><UtensilsCrossed className="size-4" style={{ color: 'var(--color-muted)' }} /></div>
                )}
                <span className="min-w-0 flex-1 truncate text-sm" style={{ color: 'var(--color-ink)' }}>{r.name}</span>
                {busy === r.id ? <Loader2 className="size-4 animate-spin" style={{ color: 'var(--color-muted)' }} /> : <Plus className="size-4" style={{ color: 'var(--color-accent)' }} />}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
